import {Helmet} from '@dr.pogodin/react-helmet';
import {openDeleteDialog} from 'platform/components';
import {match} from 'ts-pattern';

import {isArray, noop} from 'ramda-adjunct';

import {
  useDeleteEmployeeWorkTeamMutation,
  usePostEmployeeWorkTeamSetAsActiveMutation,
  usePostEmployeeWorkTeamSetAsInactiveMutation,
} from '@dms/api/customer';
import i18n from '@dms/i18n';
import {settingsRoutes, testIds} from '@dms/routes';
import {SettingsTemplate} from '@dms/settings/shared';
import {DmsDataGrid, handleApiError} from '@dms/shared';

import {composePath, useNavigate} from 'shared/web';

import type {ActionCallback} from 'features/eag-grid';

export function EmployeeWorkTeamsOverview() {
  const navigate = useNavigate();

  const [deleteEmployeeWorkTeam] = useDeleteEmployeeWorkTeamMutation();
  const [postEmployeeWorkTeamSetAsActive] = usePostEmployeeWorkTeamSetAsActiveMutation();
  const [postEmployeeWorkTeamSetAsInactive] = usePostEmployeeWorkTeamSetAsInactiveMutation();

  const actionCallback: ActionCallback = ({actionKey, rowId, refreshData}) => {
    const id = isArray(rowId) ? rowId[0] : rowId;

    match(actionKey)
      .with('redirectDetail', () =>
        navigate(composePath(settingsRoutes.employeeWorkTeamsDetail, {params: {id}}))
      )
      .with('setAsInactive', () =>
        postEmployeeWorkTeamSetAsInactive({workTeamId: id})
          .unwrap()
          .then(() => refreshData())
          .catch(handleApiError)
      )
      .with('setAsActive', () =>
        postEmployeeWorkTeamSetAsActive({workTeamId: id})
          .unwrap()
          .then(() => refreshData())
          .catch(handleApiError)
      )
      .with('delete', () =>
        openDeleteDialog({
          onConfirm: () =>
            deleteEmployeeWorkTeam({id})
              .unwrap()
              .then(() => refreshData())
              .catch(handleApiError),
        })
      )
      .otherwise(noop);
  };

  return (
    <>
      <Helmet title={i18n.t('entity.employeeWorkTeams.labels.title')} />
      <SettingsTemplate
        header={{
          title: i18n.t('entity.employeeWorkTeams.labels.title'),
          actions: [
            {
              type: 'button',
              onClick: () => navigate(settingsRoutes.employeeWorkTeamsCreate),
              title: i18n.t('entity.employeeWorkTeams.labels.newWorkTeam'),
            },
          ],
        }}
        data-testid={testIds.settings.employeeWorkTeams('settingsTemplate')}
      >
        <DmsDataGrid
          gridCode="employee-work-team"
          actionCallback={actionCallback}
          data-testid={testIds.settings.employeeWorkTeams('employee-work-team')}
        />
      </SettingsTemplate>
    </>
  );
}
